// ============================================================
// TYPES
// ============================================================

// Re-export Language from i18n module (single source of truth)
import { Language, i18n, getLanguage } from './i18n';
import { STORAGE_KEYS } from './types';
export { Language };

// ============================================================
// API RESPONSE TYPES
// ============================================================

export interface ModelInfo {
    id: string;
    name: string;
}

export interface ModelsResponse {
    models: ModelInfo[];
    default: string;
}

export interface SessionSummary {
    filename: string;
    session_id: string;
    created_at: string;
    initial_model: string;
    interaction_count: number;
}

export interface SessionsResponse {
    sessions: SessionSummary[];
}

export interface ToolResultSummary {
    success: boolean;
    message: string;
    data?: Record<string, unknown> | null;
}

export interface SessionMessage {
    role: string;
    content: string;
    thoughts?: string;
    tool_results?: ToolResultSummary[] | null;
}

export interface SessionHistoryResponse {
    session_id: string;
    created_at: string;
    initial_model: string;
    messages: SessionMessage[];
}

export interface ReverseGeocodeResponse {
    address: string;
    country_code?: string | null;
}

export interface AuthValidateResponse {
    valid: boolean;
    name: string | null;
    allowed_users: string[];
}

export interface User {
    id: string;
    name: string;
    created_at: string;
}

export interface UsersListResponse {
    users: User[];
}

export interface ClientTimeContext {
    local_time_iso: string;
    timezone: string | null;
    tz_offset_minutes: number;
}

// ============================================================
// UTILITY FUNCTIONS
// ============================================================

/**
 * Get a DOM element by ID with type safety.
 * Throws an error if the element is not found.
 */
export function getElement<T extends HTMLElement>(id: string): T {
    const element = document.getElementById(id);
    if (!element) {
        throw new Error(`Element with id "${id}" not found`);
    }
    return element as T;
}

/**
 * Escape HTML special characters to prevent XSS.
 */
export function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Get the client's current time context for API requests.
 */
export function getClientTimeContext(): ClientTimeContext {
    const now = new Date();

    const pad = (n: number): string => String(n).padStart(2, '0');
    const tzOffsetMinutes = now.getTimezoneOffset(); // minutes behind UTC (e.g. PST = 480)
    const offsetTotalMinutes = -tzOffsetMinutes; // minutes ahead of UTC
    const sign = offsetTotalMinutes >= 0 ? '+' : '-';
    const absMinutes = Math.abs(offsetTotalMinutes);
    const offsetHours = Math.floor(absMinutes / 60);
    const offsetMins = absMinutes % 60;

    const localIso =
        `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
        `T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}` +
        `${sign}${pad(offsetHours)}:${pad(offsetMins)}`;

    let timezone: string | null = null;
    try {
        timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
        timezone = null;
    }

    return {
        local_time_iso: localIso,
        timezone,
        tz_offset_minutes: tzOffsetMinutes
    };
}

// ============================================================
// LOCATION HELPERS
// ============================================================

/**
 * Calculate the distance between two lat/lng points using Haversine formula.
 * Returns distance in meters.
 */
export function haversineDistance(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
): number {
    const R = 6371000; // Earth's radius in meters
    const toRad = (deg: number) => deg * (Math.PI / 180);

    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

/**
 * Normalize a country code to a two-letter uppercase string.
 */
export function normalizeCountryCode(countryCode: string | null | undefined): string | null {
    if (!countryCode) return null;
    const normalized = countryCode.trim().toUpperCase();
    return /^[A-Z]{2}$/.test(normalized) ? normalized : null;
}

/**
 * Get the preferred country code from stored location or browser locale.
 */
export function getPreferredCountryCode(): string | null {
    const stored = normalizeCountryCode(localStorage.getItem(STORAGE_KEYS.RECOMMEND_COUNTRY));
    if (stored) return stored;

    const locale = navigator.language || navigator.languages?.[0];
    if (locale && locale.includes('-')) {
        const [, region] = locale.split('-');
        const normalized = normalizeCountryCode(region);
        if (normalized) return normalized;
    }

    return null;
}

/**
 * Get the Places API language code based on current app language.
 */
export function getPlacesLanguageCode(): string {
    return getLanguage() === 'zh' ? 'zh-CN' : 'en';
}

/**
 * Get stored coordinates for the user if available.
 */
export function getStoredLocation(): { lat: number; lng: number } | null {
    const lat = Number(localStorage.getItem(STORAGE_KEYS.RECOMMEND_LAT));
    const lng = Number(localStorage.getItem(STORAGE_KEYS.RECOMMEND_LNG));
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return { lat, lng };
    }
    return null;
}

// ============================================================
// API SERVICE
// ============================================================

// Public endpoints that don't require authentication
const PUBLIC_ENDPOINTS = [
    'api/models',           // Just returns available model list
    'api/auth/validate',    // Validates auth tokens
    'api/photos/cache',     // Serves cached photos from disk
];

/**
 * Centralized API service for making HTTP requests.
 * Handles auth headers, error extraction, and streaming.
 */
export class ApiService {
    constructor(private getHeaders: () => HeadersInit) {}

    /**
     * Make a JSON API request with automatic auth headers.
     * Auth headers are automatically added unless the endpoint is in PUBLIC_ENDPOINTS.
     */
    async fetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        const isPublic = PUBLIC_ENDPOINTS.some(e => endpoint.startsWith(e));

        const headers: HeadersInit = {
            ...(options.headers || {}),
            ...(isPublic ? {} : this.getHeaders())
        };

        const response = await fetch(endpoint, { ...options, headers });

        if (!response.ok) {
            const errorDetail = await this.extractErrorDetail(response);
            throw new Error(errorDetail);
        }

        return response.json();
    }

    /**
     * Make a streaming API request.
     * Returns the Response object for manual stream handling.
     * Auth headers are automatically added.
     */
    async fetchStream(endpoint: string, options: RequestInit = {}): Promise<Response> {
        const headers: HeadersInit = {
            ...(options.headers || {}),
            ...this.getHeaders()
        };

        const response = await fetch(endpoint, { ...options, headers });

        if (!response.ok) {
            const errorDetail = await this.extractErrorDetail(response);
            throw new Error(errorDetail);
        }

        return response;
    }

    /**
     * Extract error detail from a failed response.
     * Handles JSON error responses, plain text, and fallback to HTTP status.
     */
    private async extractErrorDetail(response: Response): Promise<string> {
        // Try JSON first (FastAPI error format)
        try {
            const errorJson = await response.json();
            if (errorJson.detail) {
                // Handle FastAPI validation errors (array of errors)
                if (Array.isArray(errorJson.detail)) {
                    return errorJson.detail.map((e: { msg?: string; loc?: string[] }) =>
                        e.msg || JSON.stringify(e)
                    ).join('; ');
                }
                return String(errorJson.detail);
            }
            // Fallback to stringifying the whole error
            return JSON.stringify(errorJson);
        } catch {
            // Not JSON, try plain text
            try {
                const text = await response.text();
                if (text) return text;
            } catch {
                // Ignore
            }
        }
        // Fallback to HTTP status
        return `HTTP ${response.status}: ${response.statusText}`;
    }
}

// ============================================================
// AUTH SERVICE
// ============================================================

/**
 * Service for handling authentication logic.
 * Handles token validation and storage, but not UI.
 */
export class AuthService {
    private currentToken: string = '';

    constructor(private storageKey: string) {}

    /**
     * Get the current auth token.
     */
    getToken(): string {
        return this.currentToken;
    }

    /**
     * Set the current token and persist to storage.
     */
    setToken(token: string): void {
        this.currentToken = token;
        localStorage.setItem(this.storageKey, token);
    }

    /**
     * Load saved token from storage (does not validate).
     */
    loadSavedToken(): string | null {
        return localStorage.getItem(this.storageKey);
    }

    /**
     * Validate a token against the API.
     */
    async validateToken(token: string): Promise<boolean> {
        try {
            const response = await fetch('api/auth/validate', {
                method: 'POST',
                headers: { 'X-Auth-Token': token }
            });
            if (!response.ok) return false;
            const data: AuthValidateResponse = await response.json();
            return data.valid;
        } catch {
            return false;
        }
    }

    /**
     * Try to restore a valid token from storage.
     * Returns the token if valid, null otherwise.
     */
    async tryRestoreToken(): Promise<string | null> {
        const saved = this.loadSavedToken();
        if (saved && await this.validateToken(saved)) {
            this.currentToken = saved;
            return saved;
        }
        return null;
    }
}

// ============================================================
// LOCATION SERVICE
// ============================================================

/**
 * Service for handling geolocation.
 */
export class LocationService {
    /**
     * Get the current position using the Geolocation API.
     */
    getCurrentPosition(options?: PositionOptions): Promise<GeolocationPosition> {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error('Geolocation not supported'));
                return;
            }

            navigator.geolocation.getCurrentPosition(
                resolve,
                reject,
                options ?? { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
            );
        });
    }

    /**
     * Get human-readable error message for geolocation errors.
     */
    getErrorMessage(error: GeolocationPositionError): string {
        switch (error.code) {
            case error.PERMISSION_DENIED:
                return i18n.geoPermissionDenied;
            case error.POSITION_UNAVAILABLE:
                return i18n.geoPositionUnavailable;
            case error.TIMEOUT:
                return i18n.geoTimeout;
            default:
                return i18n.geoUnknownError;
        }
    }

    /**
     * Reverse geocode coordinates to an address.
     */
    async reverseGeocode(lat: number, lng: number, api: ApiService): Promise<ReverseGeocodeResponse> {
        try {
            const data = await api.fetch<ReverseGeocodeResponse>(`api/reverse-geocode?lat=${lat}&lng=${lng}`);
            return data;
        } catch (error) {
            console.error('Error reverse geocoding:', error);
            throw error;
        }
    }
}
